import { useState } from 'react';
import {
  Callout,
  Chip,
  Empty,
  Field,
  FilterBar,
  Loading,
  Panel,
  SearchRow,
  Select,
  TableActions,
  TableScroll,
  logoSrc,
  rowOpen,
  useLoad,
} from '@affdice/portal-kit';

import type { PlatformApi } from '../../client.ts';
import { t } from '../../messages.ts';
import { brandMatches, brandStanding, type BrandStateFilter } from '../entity-actions.ts';
import { Failure, STANDARD_BRAND_HEX } from '../shared.tsx';
import { BrandDetailModal } from './BrandDetailModal.tsx';
import { BrandLogoThumb } from './BrandLogoThumb.tsx';

type BrandRow = Awaited<ReturnType<PlatformApi['platformBrands']>>['items'][number];

/**
 * Every brand, across operators — one row per brand, deleted ones last and
 * marked, so a deleted brand can be found to restore or purge. A row opens the
 * brand's own popup: its figures, enrolled affiliates, settings and the
 * actions its state and the viewer's permissions allow.
 */
export function Brands({ api }: { api: PlatformApi }) {
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [operatorId, setOperatorId] = useState('');
  const [stateFilter, setStateFilter] = useState<BrandStateFilter>('all');
  const [version, setVersion] = useState(0);
  const [open, setOpen] = useState<BrandRow | null>(null);
  // Re-reading the list while a popup is open would swap the list for its
  // placeholder behind it; a change made in the popup refreshes on the way out.
  const [dirty, setDirty] = useState(false);
  const loaded = useLoad(
    () => api.platformBrands(query || undefined, operatorId || undefined),
    [query, operatorId, version],
  );
  const operators = useLoad(() => api.operators(), []);
  const operatorOptions = operators.state === 'ready' ? operators.value.items : [];
  const shown = loaded.state === 'ready'
    ? loaded.value.items.filter((b) => brandMatches(b, stateFilter))
    : [];

  return (
    <>
      <div className="page-head">
        <h1>{t('ent.brands.title')}</h1>
      </div>

      <SearchRow
        value={searchInput}
        onChange={setSearchInput}
        onSearch={() => setQuery(searchInput.trim())}
        placeholder={t('ent.brands.searchPlaceholder')}
      />

      <FilterBar>
        <Field label={t('common.operator')}>
          <Select
            value={operatorId}
            onChange={setOperatorId}
            options={[
              { value: '', label: t('aff.list.everyOperator') },
              ...operatorOptions.map((o) => ({ value: o.id, label: o.display_name })),
            ]}
          />
        </Field>
        <Field label={t('common.status')}>
          <Select
            value={stateFilter}
            onChange={(v) => setStateFilter(v as BrandStateFilter)}
            options={[
              { value: 'all', label: t('ent.brands.filter.all') },
              { value: 'active', label: t('ent.brands.filter.active') },
              { value: 'inactive', label: t('ent.brands.filter.inactive') },
              { value: 'deleted', label: t('ent.brands.filter.deleted') },
            ]}
          />
        </Field>
      </FilterBar>

      {loaded.state === 'loading' ? <Loading rows={2} /> : null}
      {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}

      {loaded.state === 'ready' ? (
        <Panel
          title={t('ent.brands.panel')}
          aside={
            <div className="panel-aside">
              <span className="count count-badge">{shown.length}</span>
              <TableActions
                title={t('ent.brands.title')}
                filename="brands"
                headers={[
                  t('brand.panel.colBrand'), t('brand.panel.colCode'), t('common.operator'),
                  t('brand.panel.colCurrency'), t('brand.panel.colDomain'), t('brand.panel.colStatus'),
                  t('brand.panel.colProducts'),
                ]}
                keys={['brand', 'code', 'operator', 'currency', 'tracking_domain', 'status', 'products']}
                rows={shown.map((b) => {
                  const s = brandStanding(b);
                  return [
                    b.display_name, b.code, b.operator, b.base_currency, b.tracking_domain,
                    s.purged ? t('ent.state.purged') : s.deleted ? t('ent.state.deleted') : b.status,
                    b.products.join(' '),
                  ];
                })}
              />
            </div>
          }
        >
          {shown.length === 0 ? (
            <Empty>{query || operatorId || stateFilter !== 'all' ? t('ent.brands.noMatch') : t('ent.brands.none')}</Empty>
          ) : (
            <TableScroll>
              <table>
                <thead>
                  <tr>
                    <th>{t('brand.panel.colBrand')}</th><th>{t('common.operator')}</th>
                    <th>{t('brand.panel.colCode')}</th><th>{t('brand.panel.colCurrency')}</th>
                    <th>{t('brand.panel.colDomain')}</th><th>{t('brand.panel.colStatus')}</th>
                    <th>{t('brand.panel.colColour')}</th><th>{t('brand.panel.colProducts')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((b) => {
                    const s = brandStanding(b);
                    const gone = s.deleted || s.purged;
                    const openThis = () => setOpen(b);
                    return (
                      <tr
                        key={b.brand_id}
                        className={b.status === 'ACTIVE' && !gone ? 'row-clickable' : 'row-inactive row-clickable'}
                        onClick={openThis}
                        {...rowOpen(openThis)}
                      >
                        <td>
                          <span className="row" style={{ flexWrap: 'nowrap' }}>
                            <BrandLogoThumb
                              src={logoSrc(api.apiBaseUrl, b.logo_url)}
                              name={b.display_name}
                              accent={b.theme_accent ?? STANDARD_BRAND_HEX}
                            />
                            <span>{b.display_name}</span>
                          </span>
                        </td>
                        <td className="faint">{b.operator}</td>
                        <td className="faint">{b.code}</td>
                        <td className="num faint">{b.base_currency}</td>
                        <td className="faint">{b.tracking_domain}</td>
                        <td>
                          {s.purged ? <Chip tone="overdue">{t('ent.state.purged')}</Chip>
                            : s.deleted ? <Chip tone="neutral">{t('ent.state.deleted')}</Chip>
                              : <Chip tone={b.status === 'ACTIVE' ? 'locked' : 'neutral'}>{b.status}</Chip>}
                        </td>
                        <td>
                          <span className="row" style={{ flexWrap: 'nowrap' }}>
                            <span
                              aria-hidden="true"
                              style={{
                                display: 'inline-block', flex: 'none', width: '14px', height: '14px',
                                borderRadius: '50%', border: '1px solid var(--line-strong)',
                                background: b.theme_accent ?? STANDARD_BRAND_HEX,
                              }}
                            />
                            <span className="faint mono">{b.theme_accent ?? t('brand.panel.standard')}</span>
                          </span>
                        </td>
                        <td>{b.products.length === 0 ? <span className="faint">—</span> : b.products.join(', ')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableScroll>
          )}
        </Panel>
      ) : null}

      {open ? (
        <BrandDetailModal
          api={api}
          operatorId={open.operator_id}
          brand={open}
          onChanged={() => setDirty(true)}
          onClose={() => {
            setOpen(null);
            if (dirty) {
              setDirty(false);
              setVersion((v) => v + 1);
            }
          }}
        />
      ) : null}

      <Callout>{t('ent.brands.note')}</Callout>
    </>
  );
}
